import { isFunction, isString } from 'lodash'
import { isMoment } from 'moment'

import { API_DATE, EUROPEAN_DATE, getTimezoneLessMoment } from '../momentjs'
import { ValidationFields } from '../validators'
import { Genders } from '../api/student'
import { LanguageLocale } from './language'
import { getIdOrNull, isNonEmptyArray, isObject, validateString } from '..'

/** @enum {string} */
export const StudentProperties = Object.freeze({
  EMAIL: ValidationFields.EMAIL,
  FIRSTNAME: ValidationFields.FIRSTNAME,
  LASTNAME: ValidationFields.LASTNAME,
  GENDER: ValidationFields.GENDER,
  LANGUAGE: ValidationFields.LANGUAGE,
  SECTIONS: ValidationFields.SECTIONS,
  PHONE_NUMBER: 'phoneNumber',
  BIRTHDATE: 'birthdate',
  NISS: 'niss',
  NATIONALITY: 'nationality',
  MATRICULE: 'matricule',
  STATUS: 'status',
  SCHOOL: ValidationFields.SCHOOL,
  OWNER: 'owner',
  OWNER_TYPE: 'ownerType',
  BADGE_ASSIGNATIONS: 'badgeAssignations'
})

export const StudentValidators = Object.freeze({
  [StudentProperties.EMAIL]: s => validateString(s.email, { isEmail: true }),
  [StudentProperties.LASTNAME]: s => validateString(s.lastname, { notEmpty: true }),
  [StudentProperties.FIRSTNAME]: s => validateString(s.firstname, { notEmpty: true }),
  [StudentProperties.LANGUAGE]: s => validateString(s.language, { notEmpty: true }),
  [StudentProperties.SECTIONS]: s => isNonEmptyArray(s.sections)
})

export const StudentStatusTypes = Object.freeze({
  CURRENTLY_STUDYING: 'CURRENTLY_STUDYING',
  GRADUATED: 'GRADUATED',
  DROPPED_OUT: 'DROPPED_OUT'
})
export const StudentStatusLabels = Object.freeze([
  StudentStatusTypes.CURRENTLY_STUDYING,
  StudentStatusTypes.GRADUATED,
  StudentStatusTypes.DROPPED_OUT
])

const restrictedProperties = [StudentProperties.OWNER, StudentProperties.OWNER_TYPE, StudentProperties.BADGE_ASSIGNATIONS]

const getSchoolName = student => isNonEmptyArray(student.sections) && isObject(student.sections[0].school) ? student.sections[0].school.name : null
const getFullSectionName = (student, school = null) => {
  let section = ''

  if (isNonEmptyArray(student.sections)) {
    const localSection = school ? student.sections.find(s => s.school.id === school.id) : student.sections[0]

    if (isObject(localSection) && isObject(localSection.sectionToYear)) {
      section = `${localSection.sectionToYear.sectionName} (${localSection.sectionToYear.schoolYearName})`
    }
  }

  return section
}
const getFullSectionAbbreviation = (student, school = null) => {
  let section = ''

  if (isNonEmptyArray(student.sections)) {
    const localSection = school ? student.sections.find(s => s.school.id === school.id) : student.sections[0]

    if (isObject(localSection) && isObject(localSection.sectionToYear)) {
      section = `${localSection.sectionToYear.sectionAbbreviation} (${localSection.sectionToYear.schoolYearName})`
    }
  }

  return section
}

export const getStudentFullSection = (student, school) => {
  let section = 'NONE'

  if (student.sections && student.sections.length > 0) {
    section = student.sections[0]

    if (isObject(school)) {
      section = student.sections.find(s => s.school.id === school.id) || section
    }

    if (section.sectionToYear) {
      const { sectionToYear } = section

      section = `${sectionToYear.sectionAbbreviation} (${sectionToYear.schoolYearName})`
    } else if (section.name) {
      section = section.name
    }
  }

  return section
}

export const getLastName = student => getPropertyOrEmptyString(student, StudentProperties.LASTNAME)

export const getFirstName = student => getPropertyOrEmptyString(student, StudentProperties.FIRSTNAME)

export const getLanguage = student => getPropertyOrEmptyString(student, StudentProperties.LANGUAGE)

export const getTranslatedLanguage = (student, t) => {
  const language = getLanguage(student)
  let languageString = null

  if (isString(language) && language && isFunction(t)) {
    languageString = t('locale_languages.' + language.toLowerCase())
  }

  return languageString
}

export const getEmail = student => getPropertyOrEmptyString(student, StudentProperties.EMAIL)

export const getMatricule = student => getPropertyOrEmptyString(student, StudentProperties.MATRICULE)

export const getNiss = student => getPropertyOrNull(student, StudentProperties.NISS)

export const getPhoneNumber = student => getPropertyOrNull(student, StudentProperties.PHONE_NUMBER)

export const getBirthDate = student => getPropertyOrNull(student, StudentProperties.BIRTHDATE)

export const getEuropeanBirthDate = student => {
  const birthdate = getBirthDate(student)
  let birthdateString = null

  if (birthdate) {
    birthdateString = getTimezoneLessMoment(birthdate).format(EUROPEAN_DATE)
  }

  return birthdateString
}

export const getGender = student => getPropertyOrNull(student, StudentProperties.GENDER)

export const getTranslatedGender = (student, t) => {
  const gender = getGender(student)
  let genderString = null

  if (isString(gender) && isFunction(t)) {
    genderString = t('genders.' + gender.toLowerCase())
  }

  return genderString
}

export const getNationality = student => getPropertyOrNull(student, StudentProperties.NATIONALITY)

export const getTranslatedNationality = (student, t) => {
  const nationality = getNationality(student)
  let nationalityString = null

  if (isString(nationality) && isFunction(t)) {
    nationalityString = t('nationalities.' + nationality.toLowerCase())
  }

  return nationalityString
}

export const getLastLogin = student => isObject(student.user) && student.user.lastLogin ? getTimezoneLessMoment(student.user.lastLogin).format(EUROPEAN_DATE) : null

export const getStatus = student => getPropertyOrNull(student, 'status')

const parseStatusToBoolean = status => {
  if (typeof status === 'boolean') return status
  if (typeof status === 'number') return status === 1

  if (isString(status)) {
    const normalizedStatus = status.trim().toLowerCase()

    if (['active', 'enabled', 'true', '1'].includes(normalizedStatus)) return true
    if (['inactive', 'disabled', 'false', '0'].includes(normalizedStatus)) return false
  }

  return null
}

export const getIsActive = student => {
  const status = parseStatusToBoolean(getStatus(student))

  return status === null ? isObject(student.user) : status
}

const getDataUsage = student => student.dataUsage ?? false

export const getters = Object.freeze({
  schoolName: getSchoolName,
  sectionFullName: getFullSectionName,
  sectionFullAbbreviation: getFullSectionAbbreviation,
  lastName: getLastName,
  firstName: getFirstName,
  email: getEmail,
  matricule: getMatricule,
  niss: getNiss,
  phoneNumber: getPhoneNumber,
  birthdate: getBirthDate,
  gender: getGender,
  nationality: getNationality,
  status: getStatus,
  dataUsage: getDataUsage,
  europeanBirthDate: getEuropeanBirthDate,
  lastLogin: getLastLogin
})

const getPropertyOrEmptyString = (student, property) => student[property] || ''
const getPropertyOrNull = (student, property) => student[property] || null

export class Student {
  constructor ({
    id = -1, birthdate = null, email = '', firstname = '', lastname = '', matricule = null, nationality = null, phoneNumber = null, gender = Genders.OTHER,
    language = new LanguageLocale().languageTag, sections = [], niss = null, user = null, status = StudentStatusTypes.CURRENTLY_STUDYING, owner = null, ownerType = null,
    badgeAssignations = []
  } = {}) {
    this.id = id
    this.birthdate = isString(birthdate) ? getTimezoneLessMoment(birthdate) : birthdate
    this.email = email
    this.firstname = firstname
    this.lastname = lastname
    this.matricule = matricule
    this.nationality = nationality
    this.phoneNumber = phoneNumber
    this.gender = gender
    this.language = language
    this.sections = sections
    this.niss = niss
    this.user = user
    this.status = status
    this.owner = owner
    this.ownerType = ownerType
    /** Read only, served by the `badge-assignations` serialization context. */
    this.badgeAssignations = badgeAssignations
  }

  toRequestBody = () => {
    const body = {}

    Object.entries(this).forEach(([property, value]) => {
      if (isMoment(value)) {
        body[property] = value.format(API_DATE)
      } else if (!isFunction(value)) {
        if (isObject(value)) {
          body[property] = getIdOrNull(value)
        } else if (!restrictedProperties.includes(property)) {
          body[property] = value
        }
      }
    })

    return body
  }
}
